import api from './api.service';

export interface CustomerOutstandingDetailRow {
  invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  grand_total: number;
  paid_amount: number;
  balance_due: number;
  overdue_days: number;
  due_days: number;
  description?: string;
  customer_id: string;
  customer_name: string;
  project_id: string | null;
  invoice_type: string;
}

export interface SupplierOutstandingDetailRow {
  invoice_id: string;
  invoice_number: string;
  supplier_invoice_number: string;
  supplier_invoice_date: string;
  due_date: string;
  grand_total: number;
  paid_amount: number;
  balance_due: number;
  overdue_days: number;
  due_days: number;
  description?: string;
  supplier_id: string;
  supplier_name: string;
}

export interface PendingApprovalPaymentVoucherRow {
  id: string;
  voucher_number: string;
  voucher_date: string;
  amount: number;
  status: string;
}

export interface PendingApprovalJournalVoucherRow {
  id: string;
  voucher_number: string;
  voucher_date: string;
  total_debit: number;
  status: string;
}

export interface PendingApprovalGrnRow {
  id: string;
  grn_number: string;
  grn_date: string;
  supplier_id: string;
  grn_status: string;
}

export interface GrnNotInvoicedRow {
  id: string;
  grn_number: string;
  grn_date: string;
  supplier_id: string;
  supplier_name?: string;
}

export interface CustomerAdvanceReadyRow {
  sales_order_id: string;
  sales_order_number: string;
  customer_id: string;
  customer_name: string;
  advance_cap: number;
  advance_invoiced: number;
  remaining_advance: number;
  link_path: string;
}

export interface PoReadyForAdvanceRequestRow {
  purchase_order_id: string;
  po_number: string;
  supplier_id: string;
  supplier_name: string;
  advance_cap: number;
  remaining_request_capacity: number;
  link_path: string;
}

export interface SarReadyForPaymentRow {
  id: string;
  advance_request_number: string;
  purchase_order_id: string;
  po_number: string;
  supplier_id: string;
  supplier_name: string;
  requested_amount: number;
  paid_amount: number;
  balance_to_pay: number;
  link_path: string;
}

export interface SupplierAdvanceReadyQueues {
  po_ready_for_advance_request: PoReadyForAdvanceRequestRow[];
  sar_ready_for_payment: SarReadyForPaymentRow[];
}

export interface SalesInvoiceReadyRow {
  id: string;
  delivery_note_number: string;
  delivery_date: string;
  sales_order_id: string;
  sales_order_number: string;
  customer_id: string;
  customer_name?: string;
  link_path: string;
}

export interface SupplierInvoiceReadyRow {
  id: string;
  grn_number: string;
  grn_date: string;
  supplier_id: string;
  supplier_name?: string;
  po_id?: string;
  po_number?: string;
  link_path: string;
}

export interface MonthEndReadiness {
  draft_receipts: number;
  draft_payments: number;
  draft_journals: number;
  unposted_journals: number;
  open_ar_sources: number;
  open_ap_sources: number;
}

export interface SupplierAdvanceRecoveryQueueRow {
  exposure_id: string;
  purchase_order_id: string;
  po_number: string;
  supplier_id: string;
  supplier_name?: string;
  supplier_advance_request_id: string;
  advance_request_number: string;
  remaining_refundable_balance: number;
  recovery_status: string;
  exposure_age_days: number;
  cancellation_date?: string;
  link_path: string;
}

export interface SupplierAdvanceRecoveryDashboardSummary {
  pending_count: number;
  partial_count: number;
  overdue_count: number;
  exposure_total: number;
  queue_rows: SupplierAdvanceRecoveryQueueRow[];
}

export type FinanceAgingBucket = '0-30' | '31-60' | '61-90' | '90+';

export interface FinanceAgingBucketStats {
  count: number;
  value: number;
}

export type FinanceAgingMap = Record<FinanceAgingBucket, FinanceAgingBucketStats>;

export type CollectionsPayablesQueueMode =
  | 'overdue_ar'
  | 'overdue_ap'
  | 'due_today_ar'
  | 'due_today_ap'
  | 'due_soon_ar'
  | 'due_soon_ap';

export interface CollectionsPayablesQueueResult {
  mode: CollectionsPayablesQueueMode;
  total: number;
  page: number;
  pageSize: number;
  total_value: number;
  aging: FinanceAgingMap;
  rows: Array<CustomerOutstandingDetailRow | SupplierOutstandingDetailRow>;
}

export interface FinanceDashboardActionSummary {
  overdue_customer_invoices: CustomerOutstandingDetailRow[];
  overdue_customer_invoice_count: number;
  overdue_customer_invoice_value: number;
  overdue_customer_aging: FinanceAgingMap;
  overdue_supplier_invoices: SupplierOutstandingDetailRow[];
  overdue_supplier_invoice_count: number;
  overdue_supplier_invoice_value: number;
  overdue_supplier_aging: FinanceAgingMap;
  due_soon_customer_invoices: CustomerOutstandingDetailRow[];
  due_soon_customer_invoice_count: number;
  due_soon_customer_invoice_value: number;
  due_soon_supplier_invoices: SupplierOutstandingDetailRow[];
  due_soon_supplier_invoice_count: number;
  due_soon_supplier_invoice_value: number;
  due_today_customer_invoices: CustomerOutstandingDetailRow[];
  due_today_customer_invoice_count: number;
  due_today_customer_invoice_value: number;
  due_today_supplier_invoices: SupplierOutstandingDetailRow[];
  due_today_supplier_invoice_count: number;
  due_today_supplier_invoice_value: number;
  draft_receipt_vouchers_count: number;
  draft_payment_vouchers_count: number;
  draft_journal_vouchers_count: number;
  open_ar_sources_count: number;
  open_ap_sources_count: number;
  pending_approval_payment_vouchers: PendingApprovalPaymentVoucherRow[];
  pending_approval_pv_count: number;
  pending_approval_journal_vouchers: PendingApprovalJournalVoucherRow[];
  pending_approval_jv_count: number;
  pending_approval_grns: PendingApprovalGrnRow[];
  pending_approval_grn_count: number;
  grn_not_invoiced: GrnNotInvoicedRow[];
  month_end: MonthEndReadiness;
  pending_supplier_advance_requests_count: number;
  advances_awaiting_adjustment_count: number;
  supplier_advance_exposure: number;
  pending_supplier_bill_payment_pv_count: number;
  payment_commitments_count: number;
  customer_advance_ready: CustomerAdvanceReadyRow[];
  customer_advance_ready_count: number;
  supplier_advance_ready: SupplierAdvanceReadyQueues;
  po_ready_for_sar_count: number;
  sar_ready_for_payment_count: number;
  supplier_advance_recovery: SupplierAdvanceRecoveryDashboardSummary;
  sales_invoices_ready: SalesInvoiceReadyRow[];
  sales_invoices_ready_count: number;
  supplier_invoices_ready: SupplierInvoiceReadyRow[];
  supplier_invoices_ready_count: number;
  cash_and_bank_balance: number;
}

function emptyAging(): FinanceAgingMap {
  return {
    '0-30': { count: 0, value: 0 },
    '31-60': { count: 0, value: 0 },
    '61-90': { count: 0, value: 0 },
    '90+': { count: 0, value: 0 },
  };
}

function normalizeAging(raw: unknown): FinanceAgingMap {
  const base = emptyAging();
  if (!raw || typeof raw !== 'object') return base;
  const src = raw as Record<string, { count?: number; value?: number }>;
  for (const key of Object.keys(base) as FinanceAgingBucket[]) {
    base[key] = {
      count: Number(src[key]?.count ?? 0),
      value: Number(src[key]?.value ?? 0),
    };
  }
  return base;
}

export function emptyFinanceDashboardSummary(): FinanceDashboardActionSummary {
  return {
    overdue_customer_invoices: [],
    overdue_customer_invoice_count: 0,
    overdue_customer_invoice_value: 0,
    overdue_customer_aging: emptyAging(),
    overdue_supplier_invoices: [],
    overdue_supplier_invoice_count: 0,
    overdue_supplier_invoice_value: 0,
    overdue_supplier_aging: emptyAging(),
    due_soon_customer_invoices: [],
    due_soon_customer_invoice_count: 0,
    due_soon_customer_invoice_value: 0,
    due_soon_supplier_invoices: [],
    due_soon_supplier_invoice_count: 0,
    due_soon_supplier_invoice_value: 0,
    due_today_customer_invoices: [],
    due_today_customer_invoice_count: 0,
    due_today_customer_invoice_value: 0,
    due_today_supplier_invoices: [],
    due_today_supplier_invoice_count: 0,
    due_today_supplier_invoice_value: 0,
    draft_receipt_vouchers_count: 0,
    draft_payment_vouchers_count: 0,
    draft_journal_vouchers_count: 0,
    open_ar_sources_count: 0,
    open_ap_sources_count: 0,
    pending_approval_payment_vouchers: [],
    pending_approval_pv_count: 0,
    pending_approval_journal_vouchers: [],
    pending_approval_jv_count: 0,
    pending_approval_grns: [],
    pending_approval_grn_count: 0,
    grn_not_invoiced: [],
    month_end: {
      draft_receipts: 0,
      draft_payments: 0,
      draft_journals: 0,
      unposted_journals: 0,
      open_ar_sources: 0,
      open_ap_sources: 0,
    },
    pending_supplier_advance_requests_count: 0,
    advances_awaiting_adjustment_count: 0,
    supplier_advance_exposure: 0,
    pending_supplier_bill_payment_pv_count: 0,
    payment_commitments_count: 0,
    customer_advance_ready: [],
    customer_advance_ready_count: 0,
    supplier_advance_ready: { po_ready_for_advance_request: [], sar_ready_for_payment: [] },
    po_ready_for_sar_count: 0,
    sar_ready_for_payment_count: 0,
    supplier_advance_recovery: {
      pending_count: 0,
      partial_count: 0,
      overdue_count: 0,
      exposure_total: 0,
      queue_rows: [],
    },
    sales_invoices_ready: [],
    sales_invoices_ready_count: 0,
    supplier_invoices_ready: [],
    supplier_invoices_ready_count: 0,
    cash_and_bank_balance: 0,
  };
}

export async function getFinanceDashboardActionSummary(params?: {
  as_on_date?: string;
}): Promise<FinanceDashboardActionSummary> {
  const res = await api.get<{ data?: FinanceDashboardActionSummary }>('/finance/dashboard/action-summary', {
    params: params?.as_on_date ? { as_on_date: params.as_on_date } : undefined,
  });
  const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
  if (!data || typeof data !== 'object') {
    return emptyFinanceDashboardSummary();
  }
  const empty = emptyFinanceDashboardSummary();
  return {
    overdue_customer_invoices: Array.isArray(data.overdue_customer_invoices) ? data.overdue_customer_invoices : [],
    overdue_customer_invoice_count: Number(
      data.overdue_customer_invoice_count ?? data.overdue_customer_invoices?.length ?? 0
    ),
    overdue_customer_invoice_value: Number(data.overdue_customer_invoice_value ?? 0),
    overdue_customer_aging: normalizeAging(data.overdue_customer_aging),
    overdue_supplier_invoices: Array.isArray(data.overdue_supplier_invoices) ? data.overdue_supplier_invoices : [],
    overdue_supplier_invoice_count: Number(
      data.overdue_supplier_invoice_count ?? data.overdue_supplier_invoices?.length ?? 0
    ),
    overdue_supplier_invoice_value: Number(data.overdue_supplier_invoice_value ?? 0),
    overdue_supplier_aging: normalizeAging(data.overdue_supplier_aging),
    due_soon_customer_invoices: Array.isArray(data.due_soon_customer_invoices)
      ? data.due_soon_customer_invoices
      : [],
    due_soon_customer_invoice_count: Number(
      data.due_soon_customer_invoice_count ?? data.due_soon_customer_invoices?.length ?? 0
    ),
    due_soon_customer_invoice_value: Number(data.due_soon_customer_invoice_value ?? 0),
    due_soon_supplier_invoices: Array.isArray(data.due_soon_supplier_invoices)
      ? data.due_soon_supplier_invoices
      : [],
    due_soon_supplier_invoice_count: Number(
      data.due_soon_supplier_invoice_count ?? data.due_soon_supplier_invoices?.length ?? 0
    ),
    due_soon_supplier_invoice_value: Number(data.due_soon_supplier_invoice_value ?? 0),
    due_today_customer_invoices: Array.isArray(data.due_today_customer_invoices)
      ? data.due_today_customer_invoices
      : [],
    due_today_customer_invoice_count: Number(
      data.due_today_customer_invoice_count ?? data.due_today_customer_invoices?.length ?? 0
    ),
    due_today_customer_invoice_value: Number(data.due_today_customer_invoice_value ?? 0),
    due_today_supplier_invoices: Array.isArray(data.due_today_supplier_invoices)
      ? data.due_today_supplier_invoices
      : [],
    due_today_supplier_invoice_count: Number(
      data.due_today_supplier_invoice_count ?? data.due_today_supplier_invoices?.length ?? 0
    ),
    due_today_supplier_invoice_value: Number(data.due_today_supplier_invoice_value ?? 0),
    draft_receipt_vouchers_count: Number(data.draft_receipt_vouchers_count ?? 0),
    draft_payment_vouchers_count: Number(data.draft_payment_vouchers_count ?? 0),
    draft_journal_vouchers_count: Number(data.draft_journal_vouchers_count ?? 0),
    open_ar_sources_count: Number(data.open_ar_sources_count ?? 0),
    open_ap_sources_count: Number(data.open_ap_sources_count ?? 0),
    pending_approval_payment_vouchers: Array.isArray(data.pending_approval_payment_vouchers)
      ? data.pending_approval_payment_vouchers
      : [],
    pending_approval_pv_count: Number(
      data.pending_approval_pv_count ?? data.pending_approval_payment_vouchers?.length ?? 0
    ),
    pending_approval_journal_vouchers: Array.isArray(data.pending_approval_journal_vouchers)
      ? data.pending_approval_journal_vouchers
      : [],
    pending_approval_jv_count: Number(
      data.pending_approval_jv_count ?? data.pending_approval_journal_vouchers?.length ?? 0
    ),
    pending_approval_grns: Array.isArray(data.pending_approval_grns) ? data.pending_approval_grns : [],
    pending_approval_grn_count: Number(
      data.pending_approval_grn_count ?? data.pending_approval_grns?.length ?? 0
    ),
    grn_not_invoiced: Array.isArray(data.grn_not_invoiced) ? data.grn_not_invoiced : [],
    pending_supplier_advance_requests_count: Number(data.pending_supplier_advance_requests_count ?? 0),
    advances_awaiting_adjustment_count: Number(data.advances_awaiting_adjustment_count ?? 0),
    supplier_advance_exposure: Number(data.supplier_advance_exposure ?? 0),
    pending_supplier_bill_payment_pv_count: Number(data.pending_supplier_bill_payment_pv_count ?? 0),
    payment_commitments_count: Number(data.payment_commitments_count ?? 0),
    customer_advance_ready: Array.isArray(data.customer_advance_ready) ? data.customer_advance_ready : [],
    customer_advance_ready_count: Number(
      data.customer_advance_ready_count ?? data.customer_advance_ready?.length ?? 0
    ),
    supplier_advance_ready:
      data.supplier_advance_ready && typeof data.supplier_advance_ready === 'object'
        ? {
            po_ready_for_advance_request: Array.isArray(
              data.supplier_advance_ready.po_ready_for_advance_request
            )
              ? data.supplier_advance_ready.po_ready_for_advance_request
              : [],
            sar_ready_for_payment: Array.isArray(data.supplier_advance_ready.sar_ready_for_payment)
              ? data.supplier_advance_ready.sar_ready_for_payment
              : [],
          }
        : empty.supplier_advance_ready,
    po_ready_for_sar_count: Number(
      data.po_ready_for_sar_count ??
        data.supplier_advance_ready?.po_ready_for_advance_request?.length ??
        0
    ),
    sar_ready_for_payment_count: Number(
      data.sar_ready_for_payment_count ??
        data.supplier_advance_ready?.sar_ready_for_payment?.length ??
        0
    ),
    supplier_advance_recovery:
      data.supplier_advance_recovery && typeof data.supplier_advance_recovery === 'object'
        ? {
            pending_count: Number(data.supplier_advance_recovery.pending_count ?? 0),
            partial_count: Number(data.supplier_advance_recovery.partial_count ?? 0),
            overdue_count: Number(data.supplier_advance_recovery.overdue_count ?? 0),
            exposure_total: Number(data.supplier_advance_recovery.exposure_total ?? 0),
            queue_rows: Array.isArray(data.supplier_advance_recovery.queue_rows)
              ? data.supplier_advance_recovery.queue_rows
              : [],
          }
        : empty.supplier_advance_recovery,
    sales_invoices_ready: Array.isArray(data.sales_invoices_ready) ? data.sales_invoices_ready : [],
    sales_invoices_ready_count: Number(
      data.sales_invoices_ready_count ?? data.sales_invoices_ready?.length ?? 0
    ),
    supplier_invoices_ready: Array.isArray(data.supplier_invoices_ready)
      ? data.supplier_invoices_ready
      : [],
    supplier_invoices_ready_count: Number(
      data.supplier_invoices_ready_count ?? data.supplier_invoices_ready?.length ?? 0
    ),
    cash_and_bank_balance: Number(data.cash_and_bank_balance ?? 0),
    month_end:
      data.month_end && typeof data.month_end === 'object'
        ? {
            draft_receipts: Number(data.month_end.draft_receipts ?? 0),
            draft_payments: Number(data.month_end.draft_payments ?? 0),
            draft_journals: Number(data.month_end.draft_journals ?? 0),
            unposted_journals: Number(data.month_end.unposted_journals ?? 0),
            open_ar_sources: Number(data.month_end.open_ar_sources ?? 0),
            open_ap_sources: Number(data.month_end.open_ap_sources ?? 0),
          }
        : empty.month_end,
  };
}

export async function getFinanceCollectionsPayablesQueue(params?: {
  mode?: CollectionsPayablesQueueMode;
  page?: number;
  pageSize?: number;
  aging_bucket?: FinanceAgingBucket;
  search?: string;
  as_on_date?: string;
  due_soon_days?: number;
}): Promise<CollectionsPayablesQueueResult> {
  const res = await api.get<{ data?: CollectionsPayablesQueueResult }>(
    '/finance/dashboard/collections-payables-queue',
    { params }
  );
  const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
  return {
    mode: (QUEUE_MODES.includes(data?.mode) ? data.mode : 'overdue_ar') as CollectionsPayablesQueueMode,
    total: Number(data?.total ?? 0),
    page: Number(data?.page ?? 1),
    pageSize: Number(data?.pageSize ?? 20),
    total_value: Number(data?.total_value ?? 0),
    aging: normalizeAging(data?.aging),
    rows: Array.isArray(data?.rows) ? data.rows : [],
  };
}

const QUEUE_MODES: CollectionsPayablesQueueMode[] = [
  'overdue_ar',
  'overdue_ap',
  'due_today_ar',
  'due_today_ap',
  'due_soon_ar',
  'due_soon_ap',
];
